import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import {
  User,
  LoginCredentials,
  RegisterData,
  login as apiLogin,
  register as apiRegister,
  logout as apiLogout,
  getCurrentUser,
  getStoredToken,
  clearStoredToken,
} from '../services/auth';

interface AuthContextType {
  user: User | null;
  loading: boolean;
  error: string | null;
  isAuthenticated: boolean;
  isAdmin: boolean;
  isUserAdmin: boolean;
  isBlogger: boolean;
  login: (credentials: LoginCredentials) => Promise<boolean>;
  register: (data: RegisterData) => Promise<boolean>;
  logout: () => void;
  refreshUser: () => Promise<void>;
  clearError: () => void;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  // Check if user is authenticated on mount
  useEffect(() => {
    const initAuth = async () => {
      const token = getStoredToken();
      if (token) {
        const response = await getCurrentUser();
        if (response.data) {
          setUser(response.data);
        } else {
          // Token is invalid, clear it
          clearStoredToken();
        }
      }
      setLoading(false);
    };

    initAuth();
  }, []);

  const login = async (credentials: LoginCredentials): Promise<boolean> => {
    setError(null);
    setLoading(true);

    try {
      const loginResponse = await apiLogin(credentials);

      if (loginResponse.error) {
        setError(loginResponse.error);
        setLoading(false);
        return false;
      }

      // Get user data after successful login
      const userResponse = await getCurrentUser();

      if (userResponse.error) {
        setError(userResponse.error);
        setLoading(false);
        return false;
      }

      setUser(userResponse.data || null);
      setLoading(false);
      return true;
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Login failed';
      setError(errorMessage);
      setLoading(false);
      return false;
    }
  };

  const register = async (data: RegisterData): Promise<boolean> => {
    setError(null);
    setLoading(true);

    try {
      const registerResponse = await apiRegister(data);

      if (registerResponse.error) {
        setError(registerResponse.error);
        setLoading(false);
        return false;
      }

      // Auto-login after successful registration
      const loginSuccess = await login({
        email: data.email,
        password: data.password,
      });

      return loginSuccess;
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Registration failed';
      setError(errorMessage);
      setLoading(false);
      return false;
    }
  };

  const logout = () => {
    apiLogout();
    setUser(null);
    setError(null);
  };

  const refreshUser = async (): Promise<void> => {
    const response = await getCurrentUser();
    if (response.data) {
      setUser(response.data);
    } else {
      // If we can't get the user, they're not authenticated
      logout();
    }
  };

  const clearError = () => {
    setError(null);
  };

  const value: AuthContextType = {
    user,
    loading,
    error,
    isAuthenticated: !!user,
    isAdmin: user?.is_superuser || false,
    isUserAdmin: user?.is_superuser || user?.roles?.includes('user_admin') || false,
    isBlogger: user?.is_superuser || user?.roles?.includes('blogger') || false,
    login,
    register,
    logout,
    refreshUser,
    clearError,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

// Custom hook to use the auth context
export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
